import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Save, MapPin, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STAGE_LABELS } from "@/lib/constants";
import { useT } from "@/i18n/I18nProvider";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import PatientClinical from "@/pages/PatientClinical";
import PatientJourney from "@/components/PatientJourney";

function Field({ label, value }) {
  return (
    <div className="py-3 border-b border-slate-100">
      <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">{label}</p>
      <p className="text-sm text-slate-800 mt-1">{value || "—"}</p>
    </div>
  );
}

export default function PatientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const t = useT();
  const [p, setP] = useState(null);
  const [stage, setStage] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const editable = can("patient_records", "edit");

  useEffect(() => {
    api.get(`/patients/${id}`).then((r) => {
      setP(r.data);
      setStage(r.data.journey_stage);
      setNotes(r.data.notes || "");
    }).catch(() => toast.error(t("patients.loadError")));
  }, [id]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/patients/${id}`, { journey_stage: stage, notes });
      setP(data);
      toast.success(t("patients.updated"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const doAction = async (path, label) => {
    try { const { data } = await api.post(`/patients/${id}/${path}`); setP(data); toast.success(label); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!p)
    return <div className="p-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-navy" /></div>;

  return (
    <div className="p-6 sm:p-8 max-w-5xl">
      <button onClick={() => navigate("/patients")} data-testid="back-button" className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-6 nav-link">
        <ArrowLeft className="h-4 w-4" /> {t("patients.backToList")}
      </button>

      <div className="flex items-center gap-4 mb-8">
        <div className="h-14 w-14 rounded-xl bg-navy text-gold flex items-center justify-center font-heading text-xl font-semibold">
          {p.first_name[0]}{p.last_name[0]}
        </div>
        <div>
          <h1 className="font-heading text-2xl font-semibold text-navy">{p.first_name} {p.last_name}</h1>
          <div className="flex items-center gap-2 mt-1">
            <span className="font-mono text-sm text-slate-500">{p.patient_code}</span>
            <Badge variant="secondary">{t(`stages.${p.journey_stage}`, STAGE_LABELS[p.journey_stage] || p.journey_stage)}</Badge>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 p-6 lg:col-span-2 flex flex-wrap items-center gap-3" data-testid="arrival-ready-bar">
          <div className="flex-1 min-w-[200px]">
            <p className="text-xs uppercase tracking-wide text-slate-400">{t("patients.clinicStatus")}</p>
            <p className="text-sm text-slate-700 mt-1">
              {p.arrival_recorded_at ? `${t("patients.arrivalRecorded")} ${p.arrival_recorded_at.slice(0, 16).replace("T", " ")} (${p.arrival_recorded_by})` : t("patients.notArrived")}
              {p.ready_for_transport_at ? ` · ${t("patients.readyForTransport")} (${p.ready_for_transport_by})` : ""}
            </p>
          </div>
          <Button onClick={() => doAction("arrive", t("patients.arrivalRecorded"))} disabled={(p.arrival_recorded_at || "").slice(0, 10) === new Date().toISOString().slice(0, 10)} data-testid="patient-arrived-button" className="bg-blue-600 hover:bg-blue-700 text-white gap-2 disabled:opacity-50">
            <MapPin className="h-4 w-4" /> {t("patients.patientArrived")}
          </Button>
          <Button onClick={() => doAction("ready-for-transport", t("patients.markedReady"))} disabled={!!p.ready_for_transport_at && p.journey_stage === "ready_for_pickup"} data-testid="patient-ready-button" className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2 disabled:opacity-50">
            <CheckCircle2 className="h-4 w-4" /> {t("patients.patientReady")}
          </Button>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-heading text-lg font-medium text-navy mb-2">{t("patients.personalContact")}</h3>
          <Field label={t("patients.dob")} value={p.date_of_birth} />
          <Field label={t("patients.gender")} value={p.gender} />
          <Field label={t("patients.phone")} value={p.phone} />
          <Field label={t("patients.email")} value={p.email} />
          <Field label={t("patients.country")} value={p.country} />
          <Field label={t("patients.patientLanguage")} value={p.language} />
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-heading text-lg font-medium text-navy mb-4">{t("patients.journeyFollowup")}</h3>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>{t("patients.journeyStage")}</Label>
              <Select value={stage} onValueChange={setStage} disabled={!editable}>
                <SelectTrigger data-testid="journey-stage-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STAGE_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{t(`stages.${k}`, v)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("patients.notes")}</Label>
              <Textarea data-testid="patient-notes" value={notes} onChange={(e) => setNotes(e.target.value)} disabled={!editable} rows={5} />
            </div>
            {editable && (
              <Button onClick={save} data-testid="save-patient-button" disabled={saving} className="bg-navy hover:bg-navy-soft text-white gap-2">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {t("common.saveChanges")}
              </Button>
            )}
          </div>
        </div>
      </div>

      <PatientJourney patientId={p.id} />

      <PatientClinical patientId={p.id} />
    </div>
  );
}
